import React, { useState } from "react";
import {
  Alert,
  Button,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import {
  FallOutlined,
  RiseOutlined,
  TeamOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { saveAs } from "file-saver";
import moment from "moment";
import { useQuery, useQueryClient } from "react-query";
import MomentDatePicker from "../../../../../components/inputs/MomentDatePicker";
import { errorText, inr, payrollApi, ROLE_LABEL } from "./payrollApi";
import {
  AmountLine,
  card,
  centeredModal,
  GAP,
  Stat,
  Toolbar,
} from "./layout";

const STATUS_TAG = {
  draft: { color: "orange", text: "Draft" },
  approved: { color: "blue", text: "Approved" },
  paid: { color: "green", text: "Paid" },
};

const PAY_MODES = [
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "cheque", label: "Cheque" },
];

const PayrollRun = ({ onSetupProfiles }) => {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(moment().startOf("month"));
  const monthKey = month.format("YYYY-MM");

  const [generating, setGenerating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [paying, setPaying] = useState(null); // entry | "all"
  const [adjustForm] = Form.useForm();
  const [payForm] = Form.useForm();

  const runQuery = useQuery(["payroll-run", monthKey], async () => {
    const run = await payrollApi.runByMonth(monthKey);
    if (!run) return null;
    return payrollApi.run(run._id);
  });

  const data = runQuery.data;
  const run = data?.run;
  const entries = data?.entries || [];

  const refresh = () => queryClient.invalidateQueries(["payroll-run", monthKey]);

  const generate = async () => {
    try {
      setGenerating(true);
      await payrollApi.generate(monthKey);
      message.success(`Payroll generated for ${month.format("MMMM YYYY")}`);
      refresh();
    } catch (err) {
      message.error(errorText(err, "Could not generate payroll"));
    } finally {
      setGenerating(false);
    }
  };

  const act = async (fn, success) => {
    try {
      await fn();
      if (success) message.success(success);
      refresh();
    } catch (err) {
      message.error(errorText(err));
    }
  };

  const downloadPayslip = async (entry) => {
    try {
      const pdf = await payrollApi.payslip(entry._id);
      saveAs(pdf, `Payslip-${entry.staff_name}-${entry.month}.pdf`);
    } catch (err) {
      message.error(errorText(err, "Could not download payslip"));
    }
  };

  const submitAdjust = async () => {
    const v = await adjustForm.validateFields();
    await act(
      () => payrollApi.updateEntry(adjusting._id, v),
      "Adjustment saved"
    );
    setAdjusting(null);
  };

  const submitPay = async () => {
    const v = await payForm.validateFields();
    const body = {
      mode: v.mode,
      reference: v.reference,
      date: v.date ? v.date.format("YYYY-MM-DD") : undefined,
    };
    if (paying === "all") {
      await act(() => payrollApi.payAll(run._id, body), "Payments recorded");
    } else {
      await act(() => payrollApi.payEntry(paying._id, body), "Payment recorded");
    }
    setPaying(null);
  };

  const columns = [
    {
      title: "Staff",
      dataIndex: "staff_name",
      render: (v, r) => (
        <div>
          <div className="font-medium">{v}</div>
          <div className="text-xs text-gray-500">
            {r.designation || ROLE_LABEL[r.role] || r.role}
          </div>
        </div>
      ),
    },
    {
      title: "Days",
      render: (_, r) => {
        const c = r.calc;
        const unpaid = c.unpaid_days + c.short_time_penalty_days;
        return (
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
            <div>
              {c.present_days + c.paid_leave_days}/{c.working_days} worked or on leave
            </div>
            {unpaid > 0 ? (
              <div style={{ color: "#dc2626" }}>{unpaid} unpaid</div>
            ) : null}
          </div>
        );
      },
    },
    { title: "Gross", dataIndex: "gross", align: "right", render: inr },
    {
      title: "Deductions",
      dataIndex: "total_deductions",
      align: "right",
      render: inr,
    },
    {
      title: "Net pay",
      dataIndex: "net",
      align: "right",
      render: (v, r) => (
        <span className="font-semibold">
          {inr(v)}
          {r.manual_adjustment ? (
            <span style={{ fontSize: 12, color: "#ea580c", marginLeft: 4 }}>*</span>
          ) : null}
        </span>
      ),
    },
    {
      title: "Payment",
      render: (_, r) =>
        r.payment?.status === "paid" ? (
          <Tag color="green">Paid</Tag>
        ) : (
          <Tag>Pending</Tag>
        ),
    },
    {
      title: "",
      align: "right",
      render: (_, r) => (
        <Space size={8} wrap style={{ justifyContent: "flex-end" }}>
          <Button size="small" onClick={() => setDetail(r)}>
            Details
          </Button>
          {run?.status === "draft" ? (
            <Button
              size="small"
              onClick={() => {
                adjustForm.setFieldsValue({
                  manual_adjustment: r.manual_adjustment || 0,
                  manual_reason: r.manual_reason || "",
                });
                setAdjusting(r);
              }}
            >
              Adjust
            </Button>
          ) : null}
          {run && run.status !== "draft" && r.payment?.status !== "paid" ? (
            <Button
              size="small"
              type="primary"
              onClick={() => {
                payForm.resetFields();
                payForm.setFieldsValue({ mode: "bank_transfer", date: moment() });
                setPaying(r);
              }}
            >
              Mark paid
            </Button>
          ) : null}
          {run && run.status !== "draft" ? (
            <Button size="small" onClick={() => downloadPayslip(r)}>
              Payslip
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const status = run ? STATUS_TAG[run.status] : null;
  const pendingCount = run ? run.entry_count - run.paid_count : 0;

  return (
    <div>
      <Toolbar
        left={
          <>
            <MomentDatePicker
              picker="month"
              allowClear={false}
              value={month}
              onChange={(d) => d && setMonth(d.startOf("month"))}
            />
            {status ? (
              <Tag color={status.color} style={{ margin: 0 }}>
                {status.text}
              </Tag>
            ) : null}
          </>
        }
        right={
          <>
          {run?.status === "draft" ? (
            <>
              <Popconfirm
                title="Regenerate this payroll?"
                description="Figures are recalculated from current attendance. Manual adjustments are kept."
                onConfirm={generate}
              >
                <Button loading={generating}>Regenerate</Button>
              </Popconfirm>
              <Popconfirm
                title="Delete this draft payroll?"
                onConfirm={() =>
                  act(() => payrollApi.deleteRun(run._id), "Draft deleted")
                }
              >
                <Button danger>Delete</Button>
              </Popconfirm>
              <Popconfirm
                title="Approve payroll?"
                description="Approved payroll is frozen and can no longer be edited."
                onConfirm={() =>
                  act(() => payrollApi.approve(run._id), "Payroll approved")
                }
              >
                <Button type="primary">Approve</Button>
              </Popconfirm>
            </>
          ) : null}
          {run && run.status === "approved" && pendingCount > 0 ? (
            <Button
              type="primary"
              onClick={() => {
                payForm.resetFields();
                payForm.setFieldsValue({ mode: "bank_transfer", date: moment() });
                setPaying("all");
              }}
            >
              Mark all paid ({pendingCount})
            </Button>
          ) : null}
          </>
        }
      />

      {runQuery.isError ? (
        <Alert
          type="error"
          message="Could not load payroll"
          style={{ marginBottom: GAP.md }}
        />
      ) : null}

      {!runQuery.isLoading && !run ? (
        <div style={{ ...card, padding: "56px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: GAP.xs }}>
            No payroll for {month.format("MMMM YYYY")} yet
          </div>
          <div style={{ color: "#6b7280", marginBottom: GAP.lg }}>
            Salaries are calculated from staff attendance, leave and holidays.
          </div>
          <Button type="primary" size="large" loading={generating} onClick={generate}>
            Generate payroll
          </Button>
          <div style={{ marginTop: GAP.md }}>
            <Button type="link" onClick={onSetupProfiles}>
              Set up salary profiles
            </Button>
          </div>
        </div>
      ) : null}

      {run ? (
        <>
          {run.skipped_no_profile > 0 ? (
            <Alert
              style={{ marginBottom: GAP.lg }}
              type="warning"
              showIcon
              message={`${run.skipped_no_profile} staff member${
                run.skipped_no_profile === 1 ? " has" : "s have"
              } no salary profile and ${
                run.skipped_no_profile === 1 ? "was" : "were"
              } left out.`}
              action={
                <Button size="small" onClick={onSetupProfiles}>
                  Set up
                </Button>
              }
            />
          ) : null}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: GAP.md,
              marginBottom: GAP.lg,
            }}
          >
            <Stat
              tone="blue"
              icon={<TeamOutlined />}
              label="Staff"
              value={run.entry_count}
            />
            <Stat
              tone="green"
              icon={<RiseOutlined />}
              label="Gross"
              value={inr(run.total_gross)}
            />
            <Stat
              tone="rose"
              icon={<FallOutlined />}
              label="Deductions"
              value={inr(run.total_deductions)}
            />
            <Stat
              tone="violet"
              icon={<WalletOutlined />}
              label="Net payable"
              value={inr(run.total_net)}
              sub={`${run.paid_count}/${run.entry_count} paid`}
            />
          </div>
          <div style={{ ...card, padding: GAP.sm }}>
            <Table
              rowKey="_id"
              loading={runQuery.isLoading}
              columns={columns}
              dataSource={entries}
              pagination={{ pageSize: 20, hideOnSinglePage: true }}
              scroll={{ x: "max-content" }}
            />
          </div>
        </>
      ) : null}

      <Modal
        {...centeredModal}
        open={!!detail}
        title={detail ? `${detail.staff_name} — ${month.format("MMMM YYYY")}` : ""}
        onCancel={() => setDetail(null)}
        footer={null}
        width={620}
      >
        {detail ? <EntryDetail entry={detail} /> : null}
      </Modal>

      <Modal
        {...centeredModal}
        open={!!adjusting}
        title={adjusting ? `Adjust pay — ${adjusting.staff_name}` : ""}
        onCancel={() => setAdjusting(null)}
        onOk={submitAdjust}
        okText="Save"
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: GAP.lg }}
          message="Use a positive amount to add to net pay, negative to reduce it."
        />
        <Form form={adjustForm} layout="vertical">
          <Form.Item
            name="manual_adjustment"
            label="Adjustment (₹)"
            rules={[{ required: true }]}
          >
            <InputNumber className="w-full" step={100} />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(a, b) => a.manual_adjustment !== b.manual_adjustment}
          >
            {({ getFieldValue }) => (
              <Form.Item
                name="manual_reason"
                label="Reason"
                rules={[
                  {
                    required: Number(getFieldValue("manual_adjustment")) !== 0,
                    message: "A reason is required",
                  },
                ]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            )}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        {...centeredModal}
        open={!!paying}
        title={
          paying === "all"
            ? `Mark all ${pendingCount} pending as paid`
            : paying
            ? `Record payment — ${paying.staff_name}`
            : ""
        }
        onCancel={() => setPaying(null)}
        onOk={submitPay}
        okText="Record payment"
      >
        <Form form={payForm} layout="vertical">
          <Form.Item name="mode" label="Payment mode" rules={[{ required: true }]}>
            <Select options={PAY_MODES} />
          </Form.Item>
          <Form.Item name="date" label="Payment date">
            <MomentDatePicker className="w-full" allowClear={false} />
          </Form.Item>
          <Form.Item name="reference" label="Reference / transaction id">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const EntryDetail = ({ entry }) => {
  const c = entry.calc;
  const line = (label, value, strong, key) => (
    <AmountLine
      key={key ?? label}
      label={label}
      value={inr(value)}
      strong={strong}
    />
  );
  const heading = { fontWeight: 600, marginBottom: GAP.xs };

  return (
    <div style={{ paddingTop: GAP.xs }}>
      <Descriptions
        size="small"
        column={3}
        style={{ marginBottom: GAP.lg }}
      >
        <Descriptions.Item label="Working days">{c.working_days}</Descriptions.Item>
        <Descriptions.Item label="Present">{c.present_days}</Descriptions.Item>
        <Descriptions.Item label="On leave (paid)">{c.paid_leave_days}</Descriptions.Item>
        <Descriptions.Item label="Unpaid">{c.unpaid_days}</Descriptions.Item>
        <Descriptions.Item label="Short time">{c.short_time_days}</Descriptions.Item>
        <Descriptions.Item label="Overtime">
          {Math.round(c.ot_minutes / 6) / 10} h
        </Descriptions.Item>
      </Descriptions>

      {c.pending_days > 0 ? (
        <Alert
          style={{ marginBottom: GAP.sm }}
          type="info"
          showIcon
          message={`${c.pending_days} day(s) in this month are still to come and are not deducted.`}
        />
      ) : null}
      {c.unrecognized_days > 0 ? (
        <Alert
          style={{ marginBottom: GAP.sm }}
          type="warning"
          showIcon
          message={`${c.unrecognized_days} day(s) had an unusual attendance status and were paid — please review.`}
        />
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: GAP.xl,
          marginTop: GAP.lg,
        }}
      >
        <div>
          <div style={heading}>Earnings</div>
          {line("Basic salary", c.base_salary)}
          {(entry.allowances || []).map((a, i) =>
            line(a.name, a.amount, false, `allowance-${i}`)
          )}
          {c.overtime_amount ? line("Overtime", c.overtime_amount) : null}
          {c.bonus ? line("Bonus", c.bonus) : null}
          {entry.manual_adjustment > 0
            ? line(`Adjustment${entry.manual_reason ? ` (${entry.manual_reason})` : ""}`, entry.manual_adjustment)
            : null}
          {line("Gross", entry.gross, true)}
        </div>
        <div>
          <div style={heading}>Deductions</div>
          {c.lop_deduction
            ? line(
                `Loss of pay (${c.unpaid_days + c.short_time_penalty_days} d)`,
                c.lop_deduction
              )
            : null}
          {c.prorata_deduction
            ? line(`Not employed (${c.out_of_window_days} d)`, c.prorata_deduction)
            : null}
          {(entry.fixed_deductions || []).map((d, i) =>
            line(d.name, d.amount, false, `fixed-${i}`)
          )}
          {c.advance_recovery ? line("Advance recovery", c.advance_recovery) : null}
          {c.other_deduction ? line("Other deductions", c.other_deduction) : null}
          {entry.manual_adjustment < 0
            ? line(
                `Adjustment${entry.manual_reason ? ` (${entry.manual_reason})` : ""}`,
                -entry.manual_adjustment
              )
            : null}
          {line("Total", entry.total_deductions + Math.max(0, -entry.manual_adjustment), true)}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: GAP.lg,
          paddingTop: GAP.md,
          borderTop: "1px solid #e5e7eb",
          fontSize: 16,
          fontWeight: 600,
        }}
      >
        <span>Net pay</span>
        <span>{inr(entry.net)}</span>
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
        Per-day rate {inr(c.per_day_rate)} (base ÷ working days)
      </div>
    </div>
  );
};

export default PayrollRun;
